/**
 * Keeping a signed-in session alive across a five-minute token and a sleeping
 * phone.
 *
 * Keycloak issues access tokens with a 300-second lifespan. The app stores the
 * refresh token Keycloak hands back at login and, until now, never used it: the
 * first request made more than five minutes after signing in returned 401, the
 * user saw "session expired", and landed on the login form - whose email field
 * takes focus, which is the cursor that appears beside the header logo.
 *
 * Nothing here mints a token. `/api/auth/refresh` forwards Keycloak's own
 * `refresh_token` grant and returns whatever Keycloak decides; the backend still
 * validates every token against Keycloak's public key and signs none of its own.
 * There is no renewal timer either - the refresh happens when a request is
 * actually rejected, or when the app comes back to the foreground holding a
 * token that has already expired.
 */

import { useAuthStore } from "@/stores/authStore";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

/**
 * Endpoints that must never trigger a refresh: a 401 from them *is* the answer.
 * Retrying a failed login through the refresh path would loop, and so would a
 * logout, since the session ending below signs out again.
 */
const NO_REFRESH = [
  "/auth/login",
  "/auth/refresh",
  "/auth/register",
  "/auth/logout",
  // A shared card is opened by people without an account; its 401 asks for
  // the share's password.
  "/card/share/",
];

export function isAuthEndpoint(url?: string): boolean {
  if (!url) return false;
  return NO_REFRESH.some((path) => url.includes(path));
}

/**
 * Error codes that ride on a 401 but do not mean the session is over.
 *
 * Deleting an account asks for the password again and answers INVALID_PASSWORD
 * when it is wrong. Refreshing the token would not help - the session is
 * perfectly valid - and replaying the request would only send the same wrong
 * password a second time, after which the retry guard would sign the user out
 * for a typo.
 */
const NOT_A_SESSION_PROBLEM = new Set([
  "INVALID_PASSWORD",
  "INVALID_CREDENTIALS",
  "INVALID_FACTOR",
  "PASSWORD_REQUIRED",
]);

/** Whether a 401 body describes an expired session rather than a wrong answer. */
export function isSessionProblem(body: unknown): boolean {
  const code = (body as { error?: { code?: string } } | undefined)?.error?.code;
  return !code || !NOT_A_SESSION_PROBLEM.has(code);
}

function readAuthState(): { token?: string; refreshToken?: string } {
  try {
    const raw = localStorage.getItem("auth-storage");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.state ?? parsed ?? {};
  } catch {
    return {};
  }
}

/** Seconds left on a JWT, or null if it carries no readable `exp`. */
export function secondsUntilExpiry(token: string | null): number | null {
  if (!token) return null;
  const payload = token.split(".")[1];
  if (!payload) return null;

  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json)?.exp;
    if (typeof exp !== "number") return null;
    return exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/**
 * Single-flight: a screen that fires six queries at once produces six 401s, and
 * six parallel refreshes would race - Keycloak rotates the refresh token, so
 * five of them would present one it has already retired and the session would
 * die on the very request that was meant to save it.
 */
let inFlight: Promise<string | null> | null = null;

async function requestNewToken(): Promise<string | null> {
  const { refreshToken } = readAuthState();
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const body = await response.json();
    const accessToken: string | undefined = body?.data?.accessToken;
    if (!accessToken) return null;

    useAuthStore.setState({
      token: accessToken,
      // Keycloak rotates the refresh token; keep the old one only if it didn't.
      refreshToken: body?.data?.refreshToken ?? refreshToken,
      isAuthenticated: true,
    });

    return accessToken;
  } catch {
    // Offline counts as "could not refresh", not as "signed out". The caller
    // decides, and the offline banner already explains the missing data.
    return null;
  }
}

/** Returns a fresh access token, or null if the session cannot be renewed. */
export function refreshSession(): Promise<string | null> {
  if (!inFlight) {
    inFlight = requestNewToken().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/**
 * Clears the session and sends the user to the login page - but only once the
 * refresh has genuinely failed, and never while they are already on a public
 * page, where a redirect would interrupt something that needs no login at all.
 *
 * An expired session leaves the same health data on the device as a sign-out
 * does, so it is cleared the same way (security audit H9). Unsynced offline
 * edits are not flushed first: the server has just refused this session, so it
 * would refuse them too. The redirect waits for the clearing, because a page
 * load would abort it halfway.
 */
export async function endSession(): Promise<void> {
  try {
    // Loaded on demand: it imports the stores, and through them the API
    // clients, which import this module.
    const { secureLogout } = await import("@/utils/secureLogout");
    await secureLogout({ flushOfflineChanges: false, notify: false });
  } catch {
    // Whatever failed to clear, the session is still over.
  }
  useAuthStore.setState({
    user: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
  });

  const path = window.location.pathname;
  const isPublic =
    path === "/" ||
    path.startsWith("/emergency/") ||
    path.startsWith("/checkin/") ||
    path.startsWith("/med-access") ||
    path.startsWith("/card/share/") ||
    path.startsWith("/traveler/login") ||
    path.startsWith("/traveler/register");

  if (!isPublic) window.location.assign("/traveler/login");
}

/**
 * Renews an already-expired token when the app returns to the foreground.
 *
 * Without this, waking the phone shows a screenful of failed requests before
 * the first 401 repairs the session. With it, the repair happens before
 * anything is asked for. It fires on foreground only - it is not a timer.
 */
export function watchForForegroundExpiry(): () => void {
  const check = () => {
    if (document.visibilityState !== "visible") return;
    const { token } = readAuthState();
    if (!token) return;

    const left = secondsUntilExpiry(token);
    // 30 seconds of headroom: a token about to expire mid-request is as
    // useless as one that already has.
    if (left !== null && left < 30) void refreshSession();
  };

  document.addEventListener("visibilitychange", check);
  window.addEventListener("focus", check);
  check();

  return () => {
    document.removeEventListener("visibilitychange", check);
    window.removeEventListener("focus", check);
  };
}
